import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Home as HomeIcon, Landmark, Briefcase, MessageSquare } from "lucide-react";
import { Kicker, Heading } from "../components/ui";
import Reveal from "../components/Reveal";
import ContactForm from "../components/ContactForm";

const PATHS = [
  {
    key: "agency-sale",
    icon: Building2,
    label: "Selling an agency",
    heading: "Sell your agency",
    blurb: "For owners of estate or letting agencies exploring a confidential sale. Tell us a little about your business and we will be in touch discreetly.",
    detailLabel: "Agency name & location",
    detailPlaceholder: "[Agency name, town]",
  },
  {
    key: "portfolio-sale",
    icon: HomeIcon,
    label: "Selling a portfolio",
    heading: "Sell a property portfolio",
    blurb: "For landlords and vendors selling a residential property portfolio. Share the outline and we will respond with next steps.",
    detailLabel: "Portfolio size & location",
    detailPlaceholder: "[No. of units, region]",
  },
  {
    key: "lending",
    icon: Landmark,
    label: "Lending & funding",
    heading: "Lending & funding enquiries",
    blurb: "For banks, lenders and funding partners assessing the business. We are happy to provide further information on request.",
    detailLabel: "Institution",
    detailPlaceholder: "[Institution name]",
  },
  {
    key: "careers",
    icon: Briefcase,
    label: "Careers",
    heading: "Careers enquiries",
    blurb: "For prospective employees registering their interest. Tell us about yourself and the role you have in mind.",
    detailLabel: "Role of interest",
    detailPlaceholder: "[Role title]",
  },
  {
    key: "general",
    icon: MessageSquare,
    label: "General enquiries",
    heading: "General enquiries",
    blurb: "For press, suppliers and any other enquiry. Send us a message and we will direct it to the right person.",
    detailLabel: null,
    detailPlaceholder: "",
  },
];

const Contact = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = PATHS.find((p) => p.key === searchParams.get("path"))?.key || "agency-sale";
  const [active, setActive] = useState(initial);

  useEffect(() => {
    const param = searchParams.get("path");
    if (param && PATHS.some((p) => p.key === param)) {
      setActive(param);
    }
  }, [searchParams]);

  const activePath = PATHS.find((p) => p.key === active);

  const selectPath = (key) => {
    setActive(key);
    setSearchParams({ path: key });
  };

  return (
    <>
      <section className="bg-navy text-white pt-36 pb-20 lg:pt-44 lg:pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Kicker light>Contact</Kicker>
            <Heading as="h1" className="text-4xl md:text-5xl lg:text-6xl max-w-4xl">
              Choose the right path.
            </Heading>
            <p className="mt-8 text-lg text-steel-light max-w-2xl leading-relaxed">
              Whether you are selling a business, assessing the group, or exploring a career, choose
              the route below and your enquiry will reach the right person.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-stone">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Path selector */}
            <div className="lg:col-span-4">
              <div className="flex flex-col gap-2" role="tablist" data-testid="contact-paths">
                {PATHS.map((p) => {
                  const Icon = p.icon;
                  const isActive = p.key === active;
                  return (
                    <button
                      key={p.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => selectPath(p.key)}
                      data-testid={`contact-tab-${p.key}`}
                      className={`flex items-center gap-4 px-5 py-4 text-left border transition-colors duration-200 ${
                        isActive
                          ? "bg-navy border-navy text-white"
                          : "bg-white border-navy/10 text-navy hover:border-navy/40"
                      }`}
                    >
                      <Icon
                        size={20}
                        strokeWidth={1.5}
                        className={isActive ? "text-steel-light" : "text-steel"}
                      />
                      <span className="text-sm font-medium tracking-wide">{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active form */}
            <div className="lg:col-span-8">
              <Reveal key={active}>
                <div className="bg-white border border-navy/10 p-8 lg:p-12">
                  <h2 className="text-2xl font-semibold tracking-heading">{activePath.heading}</h2>
                  <p className="mt-2 text-steel leading-relaxed mb-8">{activePath.blurb}</p>
                  <ContactForm
                    key={active}
                    category={active}
                    detailLabel={activePath.detailLabel}
                    detailPlaceholder={activePath.detailPlaceholder}
                    testidPrefix={`contact-${active}`}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
